import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft, User, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { blogPosts } from '../data/blogs';

// Custom renderer components for ReactMarkdown
const renderers = {
  // Custom image renderer with styling
  img: ({ src, alt }) => (
    <div className="w-full flex flex-col items-center my-4 sm:my-8">
      <div className="w-full px-4 sm:px-0 sm:max-w-2xl">
        <img 
          src={src} 
          alt={alt} 
          className="w-full rounded-lg shadow-lg"
          style={{ 
            maxWidth: '100%',
            height: 'auto'
          }}
          loading="lazy"
        />
        {alt && (
          <p className="text-center text-sm text-gray-600 mt-2 italic">
            {alt}
          </p>
        )}
      </div>
    </div>
  ),
  // Enhanced heading styles
  h1: ({ children }) => (
    <h1 className="text-4xl font-bold mb-6 text-gray-900">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-3xl font-bold mb-4 mt-8 text-gray-900">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-2xl font-bold mb-3 mt-6 text-gray-900">{children}</h3>
  ),
  // Enhanced paragraph styles
  p: ({ children }) => (
    <p className="mb-4 leading-relaxed text-gray-800 text-lg">{children}</p>
  ),
  // Enhanced list styles
  ul: ({ children }) => (
    <ul className="list-disc ml-6 mb-4 space-y-2 text-gray-800">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal ml-6 mb-4 space-y-2 text-gray-800">{children}</ol>
  ),
  // Enhanced blockquote styles
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-green-500 pl-4 italic my-6 text-gray-700">
      {children}
    </blockquote>
  ),
  // Enhanced horizontal rule
  hr: () => (
    <hr className="my-8 border-t-2 border-gray-200" />
  )
};

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const post = blogPosts[id];
        if (post) {
          setContent(post.content);
          setMetadata(post.metadata);
        } else {
          setContent('# Blog post not found');
          setMetadata({
            title: 'Not Found',
            author: 'System',
            date: new Date().toLocaleDateString()
          });
        }
      } catch (error) {
        console.error('Error loading blog post:', error);
        setContent('# Error loading blog post');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Loading article...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{metadata?.title || 'Blog Post'}</title>
        <meta name="description" content={metadata?.description || 'Blog about microgreens.'} />
        <meta name="keywords" content={metadata?.keywords || 'microgreens, blog'} />
      </Helmet>
  
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="container mx-auto px-4 py-8 max-w-4xl"
      >
        <Button
          variant="outline"
          className="mb-8"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to About
        </Button>
  
        {metadata && (
          <article className="prose lg:prose-xl max-w-none">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-4xl font-bold mb-4">{metadata.title}</h1>
              <div className="flex items-center gap-6 text-gray-600 mb-8">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{metadata.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{metadata.date}</span>
                </div>
              </div>
  
              <div className="markdown-content">
                <ReactMarkdown components={renderers}>{content}</ReactMarkdown>
              </div>
            </motion.div>
          </article>
        )}
      </motion.div>
    </>
  );
};

export default BlogPost;